import React, { useEffect } from 'react';
import { PageHero } from '../components/page/PageHero';
import { IncludedList } from '../components/page/IncludedList';
import { WhyLawyerLed } from '../components/page/WhyLawyerLed';
import { FeeTransparency } from '../components/page/FeeTransparency';
import { MiniFAQ } from '../components/page/MiniFAQ';
import { FinalCTA } from '../components/home/FinalCTA';
import { Button } from '../components/ui/Button';
import { useDrawers } from '../contexts/DrawerContext';
import { FileText, Clock, ShieldCheck } from 'lucide-react';
const INCLUDED = [{
  title: 'Special conditions explained',
  body: 'Finance, building and pest, subject-to-sale and other conditions reviewed so you know what you are agreeing to.'
}, {
  title: 'Disclosure and Section 32',
  body: 'The vendor statement is read alongside the contract so disclosure gaps and concerns are identified.'
}, {
  title: 'Title and property risks',
  body: 'Easements, covenants, owners corporation and similar matters considered as part of the review.'
}, {
  title: 'Plain-English summary',
  body: 'You receive a clear explanation of the key risks and anything that needs attention before you sign.'
}];
const FAQS = [{
  q: 'How quickly can a contract be reviewed?',
  a: 'Timing depends on the document, transaction and current workload. Include your signing or auction deadline when submitting the contract so the team can assess urgency.'
}, {
  q: 'Can you review before auction?',
  a: 'Yes. The contract should ideally be reviewed before auction day because auction contracts are generally unconditional once the property is purchased. Send the contract and auction date with your enquiry.'
}, {
  q: 'Does a review create a solicitor-client relationship?',
  a: 'Submitting information does not by itself create a solicitor-client relationship. Engagement is confirmed separately, with the scope and fee set out before any work begins.'
}];
const ASSURANCES = [{
  icon: FileText,
  label: 'PDF, DOC and DOCX accepted'
}, {
  icon: Clock,
  label: 'Tell us your deadline and we assess urgency'
}, {
  icon: ShieldCheck,
  label: 'Your documents handled confidentially'
}];
export function ContractReview() {
  const {
    openUpload
  } = useDrawers();
  useEffect(() => {
    document.title = 'Contract Review and Section 32 Advice | L.A Property Law';
    window.scrollTo(0, 0);
  }, []);
  return <main data-id="element-653">
      <PageHero eyebrow="contract review" title="Understand the contract before you sign." intro="A lawyer-led review of your contract of sale and Section 32 vendor statement, with the special conditions, disclosures and key risks explained in plain English." imageSrc="https://images.unsplash.com/photo-1554224155-6726b3ff858f?auto=format&fit=crop&w=1200&q=80" imageAlt="Person reviewing a property contract document at a desk" data-id="element-654" />

      <section className="px-5 md:px-6 -mt-4 md:-mt-8" data-id="element-655">
        <div className="max-w-7xl mx-auto" data-id="element-656">
          <div className="rounded-3xl bg-surface border border-border shadow-card px-6 py-6 md:px-10 md:py-8 grid sm:grid-cols-3 gap-5" data-id="element-657">
            {ASSURANCES.map(a => {
            const Icon = a.icon;
            return <div key={a.label} className="flex items-center gap-3" data-id="element-658">
                  <span className="shrink-0 inline-flex items-center justify-center w-10 h-10 rounded-full bg-accent-soft text-accent" data-id="element-659">
                    <Icon className="w-5 h-5" data-id="element-660" />
                  </span>
                  <span className="text-sm text-ink font-medium leading-snug" data-id="element-661">
                    {a.label}
                  </span>
                </div>;
          })}
          </div>
        </div>
      </section>

      <IncludedList eyebrow="what you get" heading="A review that tells you what actually matters." intro="Not a checkbox exercise. A clear read of the documents and the risks that could affect your decision." items={INCLUDED} data-id="element-662" />

      <WhyLawyerLed data-id="element-663" />

      <FeeTransparency variant="cream" data-id="element-664" />

      <section className="px-5 md:px-6 py-20 md:py-28 bg-background" data-id="element-665">
        <div className="max-w-3xl mx-auto text-center" data-id="element-666">
          <p className="font-script text-2xl text-accent mb-1" data-id="element-667">
            ready when you are
          </p>
          <h2 className="font-serif text-3xl md:text-[2.6rem] text-ink leading-tight" data-id="element-668">
            Send the contract and we will take it from there.
          </h2>
          <p className="mt-4 text-lg text-muted-ink leading-relaxed" data-id="element-669">
            Upload the contract and Section 32, add any auction or signing
            deadline, and the team will confirm the scope and fee before any
            work begins.
          </p>
          <div className="mt-8 flex justify-center" data-id="element-670">
            <Button onClick={openUpload} variant="accent" size="lg" data-id="element-671">
              <FileText className="w-5 h-5" data-id="element-672" />
              Upload a contract
            </Button>
          </div>
        </div>
      </section>

      <MiniFAQ items={FAQS} data-id="element-673" />
      <FinalCTA data-id="element-674" />
    </main>;
}